import { useState } from 'react';
import axios from 'axios';
import { useMMAuth } from '@machinemetrics/mm-react-tools';

// "Nuke the floor" — the UI for POST /sim/nuke. The demo reset: cancel everything in flight, close the
// labour tickets, leave every machine idle.
//
// Sits beside "⟳ Reset dispatch" but is deliberately a DIFFERENT control, styled as destructive. Reset
// dispatch cannot touch started work by design; this one exists precisely to cancel it. Two buttons one
// click apart whose difference is unrecoverable in one direction, so:
//   - it demands the word NUKE typed out, not just a click-through confirm (the route enforces the same
//     token, so a mistyped curl can't do this either);
//   - the dialog states exactly what happens to started work, in the status it will actually land in.
//
// Work is CANCELLED, not COMPLETE. Closing it as complete would fabricate production, which defeats the
// point of a reset whose purpose is a floor that only made the parts you actually assigned.
//
// Colours are literal rgba/hex rather than alpha-modified `var(--cn-*)`: those tokens don't survive
// alpha modifiers, and cn-danger has known contrast debt against a filled background.
export default function NukeFloorButton({ companySlug, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped]           = useState('');
  const [busy, setBusy]             = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState(null);

  const auth  = useMMAuth();
  const token = auth?.accessToken ?? null;
  const armed = typed.trim().toUpperCase() === 'NUKE';

  const close = () => { setConfirming(false); setTyped(''); };

  const run = async () => {
    if (!armed) return;
    close();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(
        '/machinesim/api/sim/nuke',
        { companies: [companySlug], confirm: 'NUKE' },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      const r = (res.data?.results || [])[0] || {};
      if (r.error) setError(r.error);
      else setResult(r);
      onDone?.(r);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Nuke failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button
        onClick={() => setConfirming(true)}
        disabled={busy}
        title="Cancel all in-flight work for this company and leave every machine idle"
        style={{
          background: 'transparent', border: '1px solid rgba(190,70,60,0.55)',
          borderRadius: 4, padding: '6px 12px', fontSize: 12, fontFamily: 'inherit',
          color: busy ? 'var(--cn-dimmed)' : '#e8877c',
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
        }}
      >
        {busy ? 'Nuking…' : '☢ Nuke floor'}
      </button>

      {result && (
        <span style={{ fontSize: 12, color: 'var(--cn-text)' }}>
          <strong>{result.cancelledWorkOrders}</strong> WOs cancelled
          {result.cancelledOperations > 0 && ` (${result.cancelledOperations} ops)`}
          {result.released?.length > 0 && ` · ${result.released.length} machine(s) idle`}
          {result.closedLaborTickets > 0 && ` · ${result.closedLaborTickets} ticket(s) closed`}
          {result.autoAdvanceDisabled && (
            <span style={{ color: 'var(--cn-muted)' }}> · auto-advance off</span>
          )}
          {/* Surfaced, not swallowed: on a random-demand company the floor WILL refill, and a reset
              that quietly did less than it claimed is worse than one that says so. */}
          {result.demandWarning && (
            <span style={{ display: 'block', color: '#e8877c', marginTop: 4 }}>
              ⚠ {result.demandWarning}
            </span>
          )}
        </span>
      )}
      {error && <span style={{ fontSize: 12, color: 'var(--cn-danger)' }}>{error}</span>}

      {confirming && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--cn-surface)', border: '1px solid rgba(190,70,60,0.4)',
              borderRadius: 8, padding: 22, maxWidth: 500, boxShadow: '0 18px 50px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 800, color: '#e8877c', marginBottom: 10 }}>
              ☢ Nuke the floor for “{companySlug}”?
            </div>
            <p style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.55, margin: '0 0 8px' }}>
              Cancels <strong>every open work order</strong> for this company — including work that has
              already started — closes any open labour tickets, and leaves all machines idle.
            </p>
            <p style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.55, margin: '0 0 8px' }}>
              Orders are marked <strong>CANCELLED</strong>, not complete, so your production figures stay
              truthful. Completed history and lifetime part counts are untouched.
            </p>
            <p style={{ fontSize: 13, color: 'var(--cn-muted)', lineHeight: 1.55, margin: '0 0 14px' }}>
              This is not “Reset dispatch”, which leaves started work alone.
            </p>
            <label
              htmlFor="nuke-confirm"
              style={{ display: 'block', fontSize: 12, color: 'var(--cn-muted)', marginBottom: 6 }}
            >
              Type <strong style={{ color: '#e8877c' }}>NUKE</strong> to confirm:
            </label>
            <input
              id="nuke-confirm"
              autoFocus
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && armed) run(); if (e.key === 'Escape') close(); }}
              style={{
                width: '100%', boxSizing: 'border-box', background: 'var(--cn-surface2)',
                border: '1px solid rgba(190,70,60,0.35)', borderRadius: 4, padding: '7px 10px',
                fontSize: 13, fontFamily: 'inherit', color: 'var(--cn-text)', marginBottom: 18,
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={close}
                style={{
                  background: 'transparent', border: '1px solid rgba(168,144,96,0.25)', borderRadius: 4,
                  padding: '7px 14px', fontSize: 12, fontFamily: 'inherit', color: 'var(--cn-muted)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={run}
                disabled={!armed}
                style={{
                  background: armed ? '#8e2b22' : 'var(--cn-surface2)',
                  border: `1px solid ${armed ? '#8e2b22' : 'rgba(190,70,60,0.25)'}`,
                  borderRadius: 4, padding: '7px 14px', fontSize: 12, fontFamily: 'inherit',
                  fontWeight: 700, color: armed ? '#fdeceb' : 'var(--cn-dimmed)',
                  cursor: armed ? 'pointer' : 'default',
                }}
              >
                Nuke floor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
